import { FlameProgress } from "@shared/models";

/**
 * 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §3 (build-time call) / §4: the drawer's own flame chip text -- "⚡
 * Spark" bold + "90 ⚡ to the next level" grey, or the CURRENT tier name ALONE at either end of the
 * ladder. A pure function, not inline JSX, so the three ladder-end cases (not started / mid / top tier)
 * are unit-testable without rendering -- mirrors MySparks/FlameProgress/flameSubtitle.ts's own split
 * between a pure text helper and its component.
 *
 * `next_tier`'s own NAME is never shown (pavel's mystery-badges decision: the names of flame/tenure
 * levels not yet reached are hidden too) -- only the CURRENT (already-reached) tier's name ever renders.
 * `next_tier` is still read to decide whether a "to next" part exists at all.
 *
 * `FLAME_ZERO_TIER_NAME` mirrors FlameProgress.tsx's own "Flint" constant for the not-started state --
 * that one is module-private (not exported), so this is a deliberate, small duplication of one string
 * literal rather than a cross-folder import for it.
 */
const FLAME_ZERO_TIER_NAME = "Flint";

export interface FlameChipText {
  title: string;
  /** `null` when there is no "to next tier" part to show (top tier, or a malformed not-started state
   *  with no next tier) -- the chip renders no second line at all, never an empty string. */
  subtitle: string | null;
}

/**
 * @param flame - the drawer subject's flame progress (`PersonSparks.flame`).
 * @returns the chip's title (current tier name, or "Flint" if not started) and its optional "N ⚡ to
 *   the next level" subtitle (`null` at the top tier).
 */
export const flameChipText = (flame: FlameProgress): FlameChipText => {
  const { current_tier, next_tier, points_to_next } = flame;

  if (!current_tier) {
    // Not started: current_tier is null, so there is no "current" rung to name -- never claim the first
    // rung's own name here (same reasoning as FlameProgress.tsx's own FLAME_ZERO_TIER_NAME comment).
    // next_tier is the rung being worked toward.
    return {
      title: FLAME_ZERO_TIER_NAME,
      subtitle: next_tier && points_to_next !== null ? `${points_to_next} ⚡ to the next level` : null,
    };
  }

  if (!next_tier || points_to_next === null) {
    // Top tier -- name only, no "to next" part (§3's explicit build-time call).
    return { title: current_tier.name, subtitle: null };
  }

  return { title: current_tier.name, subtitle: `${points_to_next} ⚡ to the next level` };
};
